import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

// status: 'off' | 'locating' | 'granted' | 'denied' | 'unsupported'
export function useGeolocation(enabled = true) {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState(enabled ? 'locating' : 'off');
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('off');
      return undefined;
    }

    let cancelled = false;
    setStatus((s) => (s === 'granted' ? s : 'locating'));

    if (Capacitor.isNativePlatform()) {
      // Native app (Android/iOS) — use the device GPS via Capacitor, which is
      // more accurate and reliable than the WebView's browser geolocation API.
      (async () => {
        try {
          const current = await Geolocation.checkPermissions();
          const granted = current.location === 'granted'
            ? current
            : await Geolocation.requestPermissions();
          if (granted.location !== 'granted') {
            if (!cancelled) setStatus('denied');
            return;
          }
          watchIdRef.current = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
            (position, err) => {
              if (cancelled) return;
              if (err || !position) {
                setStatus((s) => (s === 'granted' ? s : 'denied'));
                return;
              }
              setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
              setStatus('granted');
            },
          );
        } catch {
          if (!cancelled) setStatus('denied');
        }
      })();
      return () => {
        cancelled = true;
        if (watchIdRef.current != null) Geolocation.clearWatch({ id: watchIdRef.current });
      };
    }

    // Web / PWA fallback — browser geolocation API.
    if (!('geolocation' in navigator)) {
      setStatus('unsupported');
      return undefined;
    }
    const webWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus('granted');
      },
      () => setStatus((s) => (s === 'granted' ? s : 'denied')),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(webWatchId);
    };
  }, [enabled]);

  return { coords, status };
}
